import * as React from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';

import type { AddressInputInitialAddress } from '@klareospace/pharos-lib-front-utils';
import { isBlankOrValidPhone } from '@klareospace/pharos-lib-front-utils/phone-validation';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  ScrollArea,
} from '@klareospace/pharos-lib-web-utils';
import { CUSTOMER_TYPE } from '@klareospace/pharos-lib-types';

import type { AddressBookEntryInput, ContactField, CustomerFormInput } from '../schemas/customer.schema';
import { hasAddressData, isAddressEntryComplete } from '../utils/customer-form.utils';
import { AddressForm } from './address-form';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AddressDialogMode = 'add' | 'edit';

export interface AddressDialogProps {
  entry: AddressBookEntryInput;
  initialAddress?: AddressInputInitialAddress;
  /** Disables the Save button while a save is already in flight (default: false) — prevents a concurrent second save. */
  isSaving?: boolean;
  mode: AddressDialogMode;
  onClose: () => void;
  onSave: (entry: AddressBookEntryInput) => void;
  open: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Placeholder non-address fields so the modal's local form satisfies AddressForm's
 * CustomerFormInput typing — AddressForm only reads `addresses.0.*`.
 */
const DIALOG_FORM_BASE_VALUES: Omit<CustomerFormInput, 'addresses'> = {
  companyName: '',
  email: '',
  firstName: '',
  lastName: '',
  loyaltyNumber: '',
  phone: { dialCode: '+33', number: '' },
  type: CUSTOMER_TYPE.CLIENT,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Mirrors the backend's `LocationDTO`/`PostalAddressDTO` contract (city, country, and coordinates
 * all required) plus the customer-form schema's contact-required rule, gating the modal's Save
 * button so an incomplete entry never reaches the drawer's PATCH.
 */
function isEntrySavable(entry: AddressBookEntryInput | undefined): boolean {
  if (!entry || !hasAddressData(entry) || !isAddressEntryComplete(entry)) return false;
  if (entry.isCustomerContact) return true;
  return !!entry.contactName?.trim() && !!entry.contactPhone?.number?.trim() && isBlankOrValidPhone(entry.contactPhone);
}

/** The dialog has no resolver, so an invalid contact phone must be surfaced here or Save stays
 * disabled with no visible reason (the error never reaches the parent drawer). */
function isContactPhoneInvalid(entry: AddressBookEntryInput | undefined): boolean {
  if (!entry || entry.isCustomerContact) return false;
  return !!entry.contactPhone?.number?.trim() && !isBlankOrValidPhone(entry.contactPhone);
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Add/edit modal for a single address-book entry. Owns a local form shaped as
 * `{ addresses: [entry] }` so AddressForm is reused unmodified at index 0. Save commits the
 * local entry back to the caller; Cancel/close discards it.
 */
export function AddressDialog({
  entry,
  initialAddress,
  isSaving = false,
  mode,
  onClose,
  onSave,
  open,
}: AddressDialogProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('customers');
  const form = useForm<CustomerFormInput>({
    defaultValues: { ...DIALOG_FORM_BASE_VALUES, addresses: [entry] },
  });

  const watchedEntry = useWatch({ control: form.control, name: 'addresses.0' });
  const canSave = isEntrySavable(watchedEntry);
  const showIncompleteHint = !!watchedEntry && hasAddressData(watchedEntry) && !isAddressEntryComplete(watchedEntry);
  const showPhoneInvalidHint = isContactPhoneInvalid(watchedEntry);

  function handleContactFieldChange<TField extends ContactField>(
    index: number,
    field: TField,
    value: AddressBookEntryInput[TField],
  ): void {
    form.setValue(`addresses.${index}.${field}` as never, value as never, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }

  function handleOpenChange(nextOpen: boolean): void {
    if (!nextOpen) onClose();
  }

  function handleSave(): void {
    onSave(form.getValues('addresses.0'));
  }

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            {mode === 'add' ? tCommon('fields.newAddressTitle') : t('addressBook.dialog.titleEdit')}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh]">
          {/* pe-4: extra clearance so focus rings aren't clipped under the scrollbar overlay. */}
          <div className="space-y-6 p-2 pe-4">
            <Form {...form}>
              <AddressForm
                form={form}
                handleContactFieldChange={handleContactFieldChange}
                index={0}
                initialAddress={initialAddress}
              />
            </Form>
          </div>
        </ScrollArea>

        {showIncompleteHint && <p className="text-sm text-muted-foreground">{t('addressBook.dialog.incomplete')}</p>}
        {showPhoneInvalidHint && (
          <p className="text-sm text-muted-foreground">{t('addressBook.dialog.phoneInvalid')}</p>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button onClick={onClose} type="button" variant="outline">
            {tCommon('actions.cancel')}
          </Button>
          <Button disabled={!canSave || isSaving} onClick={handleSave} type="button">
            {tCommon('actions.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
